export type FeatureCellValue = 'check' | 'lowOnly' | 'highOnly' | 'none';

export interface FeatureRow {
    name: string;
    description?: string;
    free: FeatureCellValue;
    low: FeatureCellValue;
    high: FeatureCellValue;
    combined: FeatureCellValue;
}

export interface FeatureCategory {
    id: string;
    title: string;
    defaultExpanded: boolean;
    features: FeatureRow[];
}

export const SUBSCRIPTION_FEATURE_CATEGORIES: FeatureCategory[] = [
    {
        id: 'tenders',
        title: 'Tenders',
        defaultExpanded: true,
        features: [
            {
                name: 'Alerts',
                description: 'Receive Tender alerts in your account and by email for opportunities that match your Tender Alert Profile (categories and locations).',
                free: 'check',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Overviews',
                description: 'See an overview for each Tender, including a headline and brief summary.',
                free: 'check',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Duplicate Notice Removal',
                description: 'We identify and remove duplicate Tender opportunities posted on multiple sites so you see each opportunity once, saving you time.',
                free: 'check',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Full Buyer Details',
                description: 'View the full Buyer information direct in your account so you can progress with your bid.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Bespoke Summaries',
                description: 'We provide a summary, helping you to make tender decisions quickly.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Search',
                description: 'Search our entire extensive tender database to understand the current market and historic trends.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            }
        ]
    },
    {
        id: 'contract-awards',
        title: 'Contract Awards',
        defaultExpanded: true,
        features: [
            {
                name: 'Alerts',
                description: 'Receive alerts in your account and by email for awarded contracts that match your Tender Alert Profile (categories and locations).',
                free: 'check',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Overviews',
                description: 'See an overview for each Contract Award, including a headline and brief summary.',
                free: 'check',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Full Buyer Details',
                description: 'View the full Buyer information direct in your account so you can understand the market and competition.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Bespoke Summaries',
                description: 'We provide a summary of content helping you to quickly understand recent Contract Awards (where the Buyer provides enough information to make this possible).',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Search',
                description: 'Search our entire extensive tender database to understand the current market and historic trends.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            }
        ]
    },
    {
        id: 'sales-pipeline',
        title: 'Sales Pipeline',
        defaultExpanded: false,
        features: [
            {
                name: 'Bespoke Tender Alerts',
                description: 'Select additional categories and locations for tailored alerting for individual team members.',
                free: 'none',
                low: 'none',
                high: 'none',
                combined: 'check'
            },
            {
                name: 'Company Tracking Alerts',
                description: 'Select companies to monitor and receive an alert when a company being tracked is awarded a contract.',
                free: 'none',
                low: 'none',
                high: 'none',
                combined: 'check'
            },
            {
                name: 'Weekly Contract Expiry Alerts',
                description: 'Identify contracts of interest and select them for notification when the contract is due to expire in 6 months.',
                free: 'none',
                low: 'none',
                high: 'none',
                combined: 'check'
            }
        ]
    },
    {
        id: 'pins',
        title: 'PINs, Pipeline and Planning Notices',
        defaultExpanded: false,
        features: [
            {
                name: 'Alerts',
                description: 'Receive alerts in your account and by email for upcoming opportunities that match your Tender Alert Profile (categories and locations).',
                free: 'none',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Overviews',
                description: 'See an overview for each Notice, including a headline and brief summary.',
                free: 'none',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Full Buyer Details',
                description: 'View the full Buyer information direct in your account so you can begin to plan for and engage with upcoming tenders.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Bespoke Summaries',
                description: 'We provide a summary helping you to identify opportunities to prioritise.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Search',
                description: 'Search our entire extensive tender database to understand the current market and likely upcoming opportunities in coordination with our other tools on offer.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            }
        ]
    },
    {
        id: 'utility',
        title: 'Utility',
        defaultExpanded: false,
        features: [
            {
                name: 'Link to Buyer website',
                description: 'Save time searching for the tender, with conveniently provided Buyer\'s website links.',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Download Buyer\'s Documents',
                description: 'Download Buyer documents directly within your account (where available).',
                free: 'none',
                low: 'lowOnly',
                high: 'highOnly',
                combined: 'check'
            },
            {
                name: 'Notes and calendar reminders',
                description: 'Add notes and set calendar reminders against Tenders/Awards to help track deadlines and follow-ups.',
                free: 'none',
                low: 'check',
                high: 'check',
                combined: 'check'
            }
        ]
    },
    {
        id: 'team-support',
        title: 'Team Support',
        defaultExpanded: false,
        features: [
            {
                name: 'Team Access',
                description: 'Full access for up to 10 users per subscription.',
                free: 'none',
                low: 'check',
                high: 'check',
                combined: 'check'
            },
            {
                name: 'Personal and Team Bookmarks',
                description: 'Track those tenders you and your team are interested in with shared visibility of bookmarks.',
                free: 'none',
                low: 'check',
                high: 'check',
                combined: 'check'
            }
        ]
    },
    {
        id: 'bid-management',
        title: 'Bid Management',
        defaultExpanded: false,
        features: [
            {
                name: 'Integrated Bid Management Platform',
                description: 'Our Bid Management platform integrates with your account to enable seamless management with your team.',
                free: 'none',
                low: 'none',
                high: 'none',
                combined: 'check'
            }
        ]
    }
];



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/subscription/subscription-features.data.ts